import ApiService from './';
import { DealCloseAndUnclosedParams, DealCustomFieldParams, DealEditParams, DealOrderParams, DealParams, TransferDealParams, UpdateDealStatusParams } from '../params/pipeline';

/**
 * @heading1: Deal API service.
 * @paragraph: This module provides a set of methods to interact with the Deal API.
 * It handles operations like Fetching Deals, deal detail, edit deal etc.
 */

const DealApi = {
    /**
     * @heading3: Fetch Deal detail
     * @paragraph: Sends a POST request to fetch deal detail based on provided parameters.
     * @param: {string} token - The user's authentication token.
     * @param: {number} id - The parameters required id on which the detail will be fetch.
     * @returns: {Promise<unknown>} - The response containing the deal detail.
     * @separator:
     */
    fetchDealDetail: (token: string, id: number) => {
        // Perform a POST request to fetch deal detail with the provided data
        return ApiService.apiHit({
            url: 'deal-details',
            method: 'post',
            token,
            data: {
                deal_id: id
            },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
  * @heading3: Create Deal
  * @paragraph: Sends a POST request to create a new deal within a stage.
  * @param: {string} token - The user's authentication token.
  * @param: {DealParams} data - The data required to create the deal.
  */
    createDeal: (token: string, data: DealParams) => {
        return ApiService.apiHit({
            url: 'deal',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Update Deal Order
     * @paragraph: Sends a POST request to update the order of deals within a stage.
     * @param: {string} token - The user's authentication token.
     * @param: {DealOrderParams} data - The new order of deals.
     */
    updateDealOrder: (token: string, data: DealOrderParams) => {
        // return {
        //     success: 1,
        //     msg: 'Deal order updated successfully'
        // };
        return ApiService.apiHit({
            url: 'update-deal-stage',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    /**
     * @heading3: Transfer Deal
     * @paragraph: Sends a POST request to transfer a deal from one stage to another.
     * @param: {string} token - The user's authentication token.
     * @param: {TransferDealParams} data - The data for the deal transfer.
     */
    transferDeal: (token: string, data: TransferDealParams) => {
        return ApiService.apiHit({
            url: 'transfer-deal',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
 * @heading3: Update Deal Status
 * @paragraph: Sends a POST request to update the status of a deal.
 * @param: {string} token - The user's authentication token.
 * @param: {UpdateDealStatusParams} data - The updated deal status details.
 */
    updateDealStatus: (token: string, data: UpdateDealStatusParams) => {
        return ApiService.apiHit({
            url: 'update-deal-status',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Get Archived Deals
     * @paragraph: Sends a POST request to retrieve archived deals.
     * @param: {string} token - The user's authentication token.
     * @param: {number} id - The ID of the pipeline or deal to fetch archived deals.
     */
    getArchievedDeals: (token: string, id: number) => {
        return ApiService.apiHit({
            url: 'deals-archived-deleted',
            method: 'post',
            token,
            data: {
                id
            },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    /**
* @heading3: Deal Custom Fields Update
* @paragraph: Sends a POST request to update custom fields for a specific deal.
* @param: {string} token - The user's authentication token.
* @param: {DealCustomFieldParams} data - The updated custom field data.
*/
    dealCustomFieldsUpdate: (token: string, data: DealCustomFieldParams) => {
        return ApiService.apiHit({
            url: 'deal-custom-fields-update',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
 * @heading3: Restore Deal
 * @paragraph: Sends a POST request to Restore Deal.
 * @param: {string} token - The user's authentication token.
 * @param: {number} id - Deal id.
 */
    restoreDeal: (token: string, id: number) => {
        return ApiService.apiHit({
            url: 'deal-restore',
            method: 'post',
            token,
            data: {
                deal_id: id
            },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
 * @heading3: Edit Deal
 * @paragraph: Sends a POST request to Edit Deal.
 * @param: {string} token - The user's authentication token.
 * @param: {DealEditParams} data - Deal details.
 */
    editDeal: (token: string, data: DealEditParams) => {
        return ApiService.apiHit({
            url: 'update-deal',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
 * @heading3: Close and Unclose Deal
 * @paragraph: Sends a POST request to close and unclose Deal.
 * @param: {string} token - The user's authentication token.
 * @param: {DealEditParams} data - Deal details.
 */
    closeAndUnCloseDeal: (token: string, data: DealCloseAndUnclosedParams, client_id: number) => {
        return ApiService.apiHit({
            url: 'deal-closed-unclosed',
            method: 'post',
            token,
            data: {
                ...data,
                client_id
            },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
 * @heading3: Fetch All Deals
 * @paragraph: Sends a GET request to fetch all Deals.
 * @param: {string} token - The user's authentication token.
 */
    fetchAllDeals: (token: string) => {
        return ApiService.apiHit({
            url: 'deals',
            method: 'get',
            token
        });
    },

//     /**
//  * @heading3: Delete Deal
//  * @paragraph: Sends a POSt request to delete single deal.
//  * @param: {string} token - The user's authentication token.
//  */
    addDealFile: (token: string, file: string, filename: string, deal_id: number) => {
        return ApiService.apiHit({
            url: 'add-deal-files',
            method: 'post',
            token,
            data: { file, filename, deal_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    fetchDealFiles: (token: string, deal_id: number) => {
        return ApiService.apiHit({
            url: 'deal-files',
            method: 'post',
            token,
            data: { deal_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    deleteDealFiles: (token: string, deal_id: number, file_id: number) => {
        return ApiService.apiHit({
            url: 'delete-deal-file',
            method: 'post',
            token,
            data: { deal_id, file_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    editDealFiles: (token: string, deal_id: number, file_id: number, filename: string) => {
        return ApiService.apiHit({
            url: 'edit-deal-files',
            method: 'post',
            token,
            data: { deal_id, file_id, filename },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    }
};

export default DealApi;
